import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Badge, Button, Tooltip, Tour, theme } from 'antd';
import { LuCircleHelp } from 'react-icons/lu';

import { TOUR_TYPE, getTour, isWithinWindow } from './tours';
import useTourSeen from './useTourSeen';

/**
 * Runs one tour from the catalogue behind a help button it owns.
 *
 * Mount it inside the surface it explains and give it a `tourId`. Nothing opens
 * by itself: the button carries a dot until the user clicks it once, and that
 * is the whole invitation. Replay works forever after; only the dot is one-shot.
 * Seen-state is per signed-in user, so this needs `User.context` above it.
 *
 * It knows nothing about the screen it sits on. The catalogue says *what* a
 * tour needs, by name; the adapter that mounts this says *how*, because it sits
 * beside the feature and may read its store or context. Three props carry that:
 *
 * - `suppressed` — the tour must not run right now: data still loading, an
 *   unconfirmed edit, a demo project. The button is disabled and an open tour
 *   closes. A blocking mask over uncommitted state is a trap, so the screen's
 *   word here is final.
 * - `stages` — named preparations, `{ name: () => void }`. A tour's `setup`
 *   stage runs before it opens (open the sidebar its steps point into); a
 *   step's `prepare` stage runs just before that step shows, going forwards or
 *   back (turn a wizard to the page the step describes — the whole page, since
 *   the previous step may be either neighbour). See `runStage` for why each is
 *   committed before targets are looked up.
 * - `cleanup` — runs whenever the tour closes, so the feature is left in a
 *   known state.
 * - `onOpenChange` — told `true` when the tour opens and `false` when it
 *   closes or unmounts open, so a screen with its own keyboard shortcuts can
 *   stand them down: the tour's arrows and Esc reach the window as well. Pass
 *   a stable function (a state setter).
 *
 * Deliberate behaviours:
 *
 * - It shows only the steps it can point at once `setup` has run. A step with
 *   its own `prepare` is trusted to produce its target when its turn comes; any
 *   other step whose target is missing is dropped, because a step with nowhere
 *   to point lands in an arbitrary corner, which reads as a bug rather than as
 *   an explanation.
 * - It renders nothing at all if no step is offerable, checked once after mount.
 *   The controls that make the button worth showing sit in the same toolbar and
 *   mount with it, so one check covers the common case.
 * - The dot clears on click, not on finishing. Someone who opened the tour and
 *   left on step one has seen the button; asking again is what the dot promised
 *   not to do.
 */

const targetSelector = target => `[data-tour="${target}"]`;

/**
 * Whether an element is somewhere a reader can actually look.
 *
 * Two ways a target fails this: `display: none`, from a collapsed layer group,
 * or a zero-size box. Both make a step land in an arbitrary corner pointing at
 * nothing, so both are skipped.
 *
 * Being scrolled out of view is deliberately *not* a failure. The sidebar body
 * is an `overflow: auto` container, so its header — and the view switch in it —
 * leaves the screen without leaving the document; those steps are worth keeping,
 * and `scrollTargetIntoView` brings them back before the step is shown.
 */
const isShowable = element => {
	if (typeof element.checkVisibility === 'function' && !element.checkVisibility()) return false;

	const rect = element.getBoundingClientRect();
	return rect.width > 0 && rect.height > 0;
};

/**
 * Brings a step's target into view before the step is positioned.
 *
 * antd scrolls for itself, but measures the target as it starts rather than once
 * the scroll has settled, which is how a step anchored to something inside the
 * sidebar ends up pinned to a corner. Scrolling here, a beat before opening,
 * means the rect antd reads is the final one. `block: 'center'` rather than
 * 'nearest' so the popover has room on either side of the target.
 */
const scrollTargetIntoView = element => {
	if (typeof element?.scrollIntoView !== 'function') return;

	element.scrollIntoView({ block: 'center', inline: 'nearest' });
};

const findTarget = target => {
	const element = document.querySelector(targetSelector(target));
	if (!element) return null;

	return isShowable(element) ? element : null;
};

// A step that prepares its own page is offerable whether or not that page is
// on screen yet; anything else has to be pointable right now.
const isOfferable = step => Boolean(step.prepare) || findTarget(step.target) !== null;

/**
 * Runs a stage and commits it, so the DOM reflects it before the caller goes
 * looking for targets. Stages are store writes and context dispatches, which
 * React would otherwise batch until after this click.
 */
const runStage = stage => {
	if (stage) flushSync(stage);
};

/**
 * Reports to Google Tag Manager. One `tour` event carrying an action, rather
 * than four event names, so the container needs configuring once and a fifth
 * action later needs no container change.
 */
const pushTourEvent = (tourId, action, stepIndex, stepTarget) => {
	if (!Array.isArray(window.dataLayer)) return;

	window.dataLayer.push({
		event: 'tour',
		tour_id: tourId,
		action,
		step_index: stepIndex,
		// Two users can be shown different steps, so the index alone doesn't
		// identify what someone was looking at when they left.
		step_target: stepTarget,
	});
};

// The default mask is light enough that the highlighted element doesn't obviously
// belong to the message next to it. A darker mask, a generous cut-out and an
// outline on the target itself make the link unmistakable.
const MASK = { color: 'rgba(0, 0, 0, 0.65)' };
const HIGHLIGHT_GAP = { offset: 8, radius: 6 };

// Appended to the button's label while the dot shows, so the unseen state
// reaches screen-reader users too rather than living in the dot's colour alone.
const DOT_SUFFIX = {
	[TOUR_TYPE.ONBOARDING]: ' (new)',
	[TOUR_TYPE.ANNOUNCEMENT]: ' (unread)',
};

// Handed to antd while the tour is closed. rc-tour resolves `steps[current]`'s
// target in a layout effect on every render and keeps resize/scroll listeners
// on it — a forced reflow per host re-render for a tour nobody can see. With no
// steps it does neither.
const NO_STEPS = [];

const AppTour = ({ tourId, suppressed = false, stages = {}, cleanup, onOpenChange }) => {
	const tour = getTour(tourId);
	const { token } = theme.useToken();
	const { seen, markSeen } = useTourSeen(tourId);

	const [open, setOpen] = useState(false);
	const [current, setCurrent] = useState(0);
	// The steps this run will show: fixed when the tour opens, so the tour can't
	// change length under the reader as sidebars come and go.
	const [visibleSteps, setVisibleSteps] = useState([]);
	// Whether the button has anything to offer on this screen. Read once the
	// surrounding toolbar has committed, which is after this component's mount.
	const [hasOfferableStep, setHasOfferableStep] = useState(false);

	useEffect(() => {
		if (!tour) return;
		setHasOfferableStep(tour.steps.some(isOfferable));
	}, [tour]);

	// Close and stand down if the screen becomes unsafe mid-tour. Same exit as
	// the close button: the feature is put back either way.
	useEffect(() => {
		if (!suppressed || !open) return;
		setOpen(false);
		cleanup?.();
	}, [suppressed, open, cleanup]);

	// The cleanup covers both a close and an unmount while open, so the screen
	// is never left believing a tour is still up.
	useEffect(() => {
		if (!open) return undefined;
		onOpenChange?.(true);
		return () => onOpenChange?.(false);
	}, [open, onOpenChange]);

	// Outline the element the current step is about. The mask alone leaves it
	// ambiguous which control a message refers to when several sit close
	// together, as they do in a toolbar.
	useEffect(() => {
		if (!open) return;

		const element = findTarget(visibleSteps[current]?.target);
		if (!element) return;

		const previous = {
			outline: element.style.outline,
			outlineOffset: element.style.outlineOffset,
		};

		element.style.outline = `2px solid ${token.colorPrimary}`;
		element.style.outlineOffset = '3px';

		return () => {
			element.style.outline = previous.outline;
			element.style.outlineOffset = previous.outlineOffset;
		};
	}, [open, current, visibleSteps, token.colorPrimary]);

	const steps = visibleSteps.map(step => ({
		title: step.title,
		description: step.description,
		placement: step.placement,
		// A getter, not an element: targets mount and unmount with their
		// sidebars and wizard pages, so this is resolved each time the step
		// is shown.
		target: () => findTarget(step.target),
	}));

	const showStep = step => {
		runStage(stages[step.prepare]);
		scrollTargetIntoView(findTarget(step.target));
	};

	const start = () => {
		// The button keeps focus under the mask, so Space or Enter would press it
		// again: an open tour carries on rather than restarting.
		if (open) return;
		markSeen();
		runStage(stages[tour.setup]);

		const offerable = tour.steps.filter(isOfferable);
		if (!offerable.length) return;

		showStep(offerable[0]);
		setVisibleSteps(offerable);
		setCurrent(0);
		setOpen(true);
		pushTourEvent(tourId, 'start', 0, offerable[0].target);
	};

	const handleChange = nextStep => {
		showStep(visibleSteps[nextStep]);
		setCurrent(nextStep);
		pushTourEvent(tourId, 'advance', nextStep, visibleSteps[nextStep]?.target);
	};

	const handleClose = () => {
		const finished = current === visibleSteps.length - 1;
		pushTourEvent(
			tourId,
			finished ? 'complete' : 'dismiss',
			current,
			visibleSteps[current]?.target
		);
		setOpen(false);
		cleanup?.();
	};

	if (!tour || !hasOfferableStep) return null;

	// An expired announcement stays available on request; only its dot is gone.
	const showDot = !seen && isWithinWindow(tour);
	const label = tour.button.label + (showDot ? DOT_SUFFIX[tour.type] : '');

	return (
		<>
			<Tooltip title={tour.button.tooltip}>
				<Badge dot={showDot}>
					<Button
						size="small"
						type="text"
						onClick={start}
						disabled={suppressed}
						aria-label={label}
						icon={<LuCircleHelp size={18} aria-hidden="true" />}
					/>
				</Badge>
			</Tooltip>

			<Tour
				open={open}
				current={current}
				onChange={handleChange}
				onClose={handleClose}
				steps={open ? steps : NO_STEPS}
				mask={MASK}
				gap={HIGHLIGHT_GAP}
				arrow={{ pointAtCenter: true }}
				disabledInteraction
			/>
		</>
	);
};

export default AppTour;
